import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { checkAllServicesHealth, type HealthStatus } from "../lib/api/health";
import { Text } from "../../ui/components/Text";
import { DarkModeToggle } from "../components/DarkModeToggle";
import * as s from "../styles/health.css";

export const Route = createFileRoute("/health")({
  component: HealthComponent,
});

function HealthComponent() {
  const { data: healthStatuses, isLoading, error } = useQuery({
    queryKey: ["health-status"],
    queryFn: checkAllServicesHealth,
    refetchInterval: 30000, // 30 seconds
    refetchIntervalInBackground: true,
  });

  return (
    <div className={s.container}>
      <div className={s.header}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Text level="h1" weight="bold" color="primary">
            Service Health Status
          </Text>
          <DarkModeToggle placement="header" />
        </div>
        <Text level="body-medium" color="muted">
          Last updated: {healthStatuses?.[0]?.timestamp ? new Date(healthStatuses[0].timestamp).toLocaleString() : 'Never'}
        </Text>
      </div>

      {isLoading && !healthStatuses && (
        <div className={s.loadingContainer}>
          <Text level="body-medium" color="muted">
            Loading health status...
          </Text>
        </div>
      )}

      {error && (
        <div className={s.errorContainer}>
          <Text level="body-medium" color="danger">
            Failed to load health status: {error instanceof Error ? error.message : 'Unknown error'}
          </Text>
        </div>
      )}

      {healthStatuses && (
        <div className={s.cardsGrid}>
          {healthStatuses.map((status) => (
            <ServiceCard key={status.service} status={status} />
          ))}
        </div>
      )}
    </div>
  );
}

function ServiceCard({ status }: { status: HealthStatus }) {
  const isHealthy = status.status === 'ok';
  const statusText = isHealthy ? 'Healthy' : 'Unhealthy';

  return (
    <div className={s.card}>
      <div className={s.cardHeader}>
        <Text level="h3" weight="bold">
          {status.service}
        </Text>
        <div className={s.statusBadge}>
          <div className={s.badgeDot({ status: isHealthy ? 'healthy' : 'unhealthy' })} />
          <Text level="body-small" weight="bold" color={isHealthy ? 'success' : 'danger'}>
            {statusText}
          </Text>
        </div>
      </div>

      <div className={s.cardBody}>

        {status.responseTime !== undefined && (
          <div className={s.statusRow}>
            <Text level="body-small" color="muted">
              Response Time:
            </Text>
            <Text level="body-medium">
              {status.responseTime}ms
            </Text>
          </div>
        )}

        {status.timestamp && (
          <div className={s.statusRow}>
            <Text level="body-small" color="muted">
              Last Checked:
            </Text>
            <Text level="body-small">
              {new Date(status.timestamp).toLocaleTimeString()}
            </Text>
          </div>
        )}

        {status.error && (
          <div className={s.errorMessage}>
            <Text level="body-small" color="danger">
              {status.error}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
}
